<template>
  <div class="overflow-y-hidden relative w-full h-[100lvh] bg-cw-green-dark">
    <GameTimer
      :timeRemaining="userPlayerProfile.actionTimeout"
      :key="userPlayerProfile.actionTimeout"
      v-if="!hasAnswered && userPlayerProfile && userPlayerProfile.actionTimeout > 0"
      class="absolute bottom-10 left-10"
    />
    <div class="w-max mt-[20vh] ml-[2.5vw] lg:ml-[10vw] flex flex-col gap-y-2.5 items-center">
      <GameCardItem :card="cardToReceive" class="px-2.5" />
      <div class="w-[50%] lg:w-[80%] flex flex-col gap-y-1.5 text-center">
        <p class="font-normal text-cw-green-muted leading-[110%]">Answer correctly to get this card</p>
        <p class="font-bold text-white">{{ activeOffer.user?.username }}</p>
      </div>
    </div>
    <div class="overflow-scroll pb-20 absolute top-0 left-0 h-[80dvh] lg:h-auto right-0 w-[32%] min-w-120 lg:min-w-80 max-w-120 mt-[5%] lg:mt-[9.6%] mx-auto grid grid-cols-1 justify-center gap-y-4 text-center">
      <p class="text-lg md:text-xl lg:text-2xl font-bold">{{ activeOffer.question }}</p>
      <button
        v-for="(option, index) in options"
        :key="index"
        :disabled="hasAnswered"
        :class="[
          getOptionBorderClass(index),
          'w-full max-md:py-3 p-3.5 border rounded-lg grid grid-cols-[max-content_1fr] gap-x-3.5 items-center disabled:cursor-not-allowed transition-all duration-200 ease-in-out'
        ]"
        @click="selectOption(index)"
      >
        <div :class="[getOptionIconBorderClass(index), 'w-[21px] h-[21px] p-0.5 border rounded-sm flex justify-center items-center transition-all duration-200 ease-in-out']">
          <template v-if="hasAnswered && index === selectedAnswerIndex">
            <CheckIcon v-if="index === correctOptionIndex && isUserCorrect" class="size-5 bg-cw-green" />
            <CloseIcon v-else class="size-5 bg-[#DC5423] text-white" />
          </template>
          <template v-if="!hasAnswered && index === selectedAnswerIndex">
            <div class="w-full h-full rounded-xs bg-white" />
          </template>
        </div>
        <p :class="['justify-self-start text-sm md:text-base font-medium text-left']">{{ option }}</p>
      </button>
      <template v-if="hasVerifiedAnswer">
        <p v-if="showSuccessMessage" class="rounded-lg p-3.5 bg-[#072C1E] text-base md:text-lg lg:text-xl font-bold text-cw-green">Yay! You got that right</p>
        <p v-else class="rounded-lg p-3.5 bg-[#42220A] text-base md:text-lg lg:text-xl font-bold text-[#DC5423]">Oops! That was not right</p>
      </template>
      <div class="grid justify-center" v-else>
        <BaseButton @click="checkAnswer" :disabled="selectedAnswerIndex == null || hasAnswered" background-color="var(--color-cw-green)" border-color="var(--color-cw-green-border-shadow)">
          <p class="w-full heightSm:!text-sm !text-base xl:!text-xl">Check answer</p>
        </BaseButton>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref } from 'vue'
import GameCardItem from '@/components/game-play/GameCardItem.vue'
import CloseIcon from '@/assets/svgs/icons/close.svg'
import CheckIcon from '@/assets/svgs/icons/check.svg'

import GameTimer from '@/components/interface/GameTimer.vue'
import { computed, inject } from 'vue'
import BaseButton from '@/components/base/BaseButton.vue'
import { useGameArea } from '@/composables/useGameArea'
import { useHelpers } from '@/composables/useHelpers'
import useSocket from '@/composables/useSocket'
import { GameEvent } from '@/types/Events'
import type { CardT } from '@/types/Game'
import { useGameStore } from '@/stores/game'
import { storeToRefs } from 'pinia'
import { useAppStore } from '@/stores/app'
import { useSound } from '@/composables/useSound'

const showLoader = inject('showLoader') as InjectedFunctionT
const hideLoader = inject('hideLoader') as InjectedFunctionT

const { deferGameState } = useAppStore()
const { gameState, userPlayerProfile } = storeToRefs(useGameStore())
const { tradeOfferPlayerId, setActiveFinalisingTradeScreen } = useGameArea()
const { handleCustomError, sleep } = useHelpers()
const { getCardById, getPlayerById, resumeGameState } = useGameStore()
const socket = useSocket()
const { playCorrectAnswer, playWrongAnswer } = useSound()

const activeOffer = computed(() => {
  const offer = gameState.value!.activeTrade!.offers.find(offer => offer.playerId == tradeOfferPlayerId.value)
  return {
    ...offer,
    user: getPlayerById(offer!.playerId)
  }
})

const hasAnswered = ref(false)
const hasVerifiedAnswer = ref(false)
const showSuccessMessage = ref(false)

const options = computed(() => {
  return activeOffer.value!.options
})

const cardToReceive = computed(() => {
  return getCardById(activeOffer.value!.cardId as string) as CardT
})

const selectedAnswerIndex = ref<number | null>(null)

const correctOptionIndex = computed(() => {
  return activeOffer.value!.answerIndex
})

const isUserCorrect = computed(() => {
  return hasAnswered.value && selectedAnswerIndex.value === correctOptionIndex.value
})

const getOptionBorderClass = (index: number) => {
  if (hasAnswered.value) {
    if (index === selectedAnswerIndex.value) {
      if (isUserCorrect.value) {
        return 'border-cw-green'
      } else {
        return 'border-[#DC5423]'
      }
    } else {
      return 'border-[#527A6B]'
    }
  } else if (index === selectedAnswerIndex.value) {
    return 'border-white'
  } else {
    return 'border-[#527A6B]'
  }
}

const getOptionIconBorderClass = (index: number) => {
  if (hasAnswered.value) {
    if (index === selectedAnswerIndex.value) {
      if (isUserCorrect.value) {
        return 'border-cw-green bg-cw-green'
      } else {
        return 'border-[#DC5423] bg-[#DC5423]'
      }
    } else {
      return 'border-[#527A6B]'
    }
  } else if (index === selectedAnswerIndex.value) {
    return 'border-white'
  } else {
    return 'border-[#527A6B]'
  }
}

const selectOption = (index: number) => {
  if (hasAnswered.value) return
  selectedAnswerIndex.value = index
}

const finaliseTrade = async () => {
  await sleep(2000)

  if (showSuccessMessage.value) {
    setActiveFinalisingTradeScreen('TRADE_CONGRATULATORY_SCREEN')
  } else {
    await sleep(3000)
    try {
      await socket.sendEventAsync<CardT[]>(GameEvent.CompleteTrade)
      resumeGameState()
    } catch (error) {
      handleCustomError(error)
      resumeGameState()
    }
  }
}

const checkAnswer = async () => {
  hasAnswered.value = true
  try {
    deferGameState()
    showLoader()
    const { data } = await socket.sendEventAsync<{ isCorrect: boolean }>(GameEvent.AnswerQuestion, {
      answerIndex: selectedAnswerIndex.value
    })
    if (data.isCorrect) {
      showSuccessMessage.value = true
      playCorrectAnswer()
    } else {
      showSuccessMessage.value = false
      playWrongAnswer()
    }
  } catch (error: any) {
    handleCustomError(error)
  } finally {
    hasVerifiedAnswer.value = true
    hideLoader()
    finaliseTrade()
  }
}
</script>
